import useWebSocketSubscription from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/hooks/useWebSocketSubscription";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { setUnreadChatCount, useUnreadChatCount } from "@/hooks/queries/lazo-chat-conversation/useLazoChatConversationQueries";
import { invalidateNotificationsCount, useNotificationsCount } from "@/hooks/queries/notification/useNotificationQueries";
import NotificationsWebSocketClient from "@/utils/websocket/NotificationsWebSocketClient";
import { createContext, useContext } from "react";

type NotificationsContextType = {
  amountUnread: number;
  amountChatUnread: number;
  updateAmount: () => void;
};

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsContextProvider");
  }

  return context;
};

export function NotificationsContextProvider({ children }) {
  const { data: amountUnread, refetch: refetchAmountUnread } = useNotificationsCount();
  const { data: amountChatUnread, refetch: refetchAmountChatUnread } = useUnreadChatCount();

  function handleWsMessage(jsonMessage: string) {
    const message = JSON.parse(jsonMessage);

    switch (message.type) {
      case "NEW_NOTIFICATION":
        invalidateNotificationsCount();
        document.dispatchEvent(new CustomEvent("updateDueToNewNotificationReceived"));
        if (message.content.notification?.type === "DOCUMENT_PROCESS") {
          NotificationService.info(message.content.notification?.title ?? "You have a new notification");
        }
        return;
      case "NEW_MESSAGE":
        setUnreadChatCount(message.content.unreadChatMessages);
        NotificationService.info(`You received a new message from ${message.content.userName}`);
        document.dispatchEvent(new CustomEvent("updateDueToNewMessageReceived"));
        return;
      case "CONVERSATION_READ":
        setUnreadChatCount(message.content.unreadChatMessages);
        return;
    }
  }

  useWebSocketSubscription(NotificationsWebSocketClient, AuthService.getUser()?.externalId!, handleWsMessage);

  function updateAmount() {
    refetchAmountUnread();
    refetchAmountChatUnread();
  }

  return <NotificationsContext.Provider value={{ amountUnread, amountChatUnread, updateAmount }}>{children}</NotificationsContext.Provider>;
}
